import React, { useEffect, useState } from 'react'
import SideBar from '../components/SideBar'
import DetailsHeaderBar from '../components/DetailsHeaderBar'
import { useLocation, useNavigate } from 'react-router-dom';
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import axios from "axios";
import { API_URL } from "../constant";

function EditProduct() {
  const [step, setStep] = useState(1);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const normalizeId = (val) => {
    if (val === undefined || val === null || val === "") return null;
    const raw = typeof val === "object" ? val.id ?? val.value : val;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
  };

  const getLabel = (val) => {
    if (val === undefined || val === null || val === "") return null;
    if (typeof val === "object") return val.label ?? val.name ?? null;
    return val;
  };

  // Remove hardcoded token and use from localStorage
  const getToken = () => localStorage.getItem("auth_token");

  const apiClient = React.useMemo(
    () =>
      axios.create({
        baseURL: API_URL || "https://api.topcoinuk.com/api",
        headers: {
          Accept: "application/json",
        },
      }),
    []
  );

  // Add token interceptor
  apiClient.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle 401 responses
  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem("auth_token");
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }
  );

  const location = useLocation();
  const product = location.state?.product || {};
  const navigate = useNavigate();

  const toOption = (val, labelKey) => {
    if (!val) return null;
    if (typeof val === "object") {
      const value = val.value ?? val.id ?? val[labelKey] ?? val;
      const label = val.label ?? val.name ?? val.retail_name ?? val[labelKey] ?? String(val);
      return { value, label };
    }
    return { value: val, label: val };
  };

  const normalizeUrl = (src) => {
    if (!src) return null;
    if (typeof src !== "string") return null;
    const localhostNoPort = /^http:\/\/localhost(?!:)/i;
    if (src.startsWith("http://") || src.startsWith("https://")) {
      return src.replace(localhostNoPort, "https://api.topcoinuk.com");
    }
    if (src.startsWith("blob:") || src.startsWith("data:")) return src;
    const base = API_URL || "https://api.topcoinuk.com/api";
    // Remove /api from base for image URLs
    const baseWithoutApi = base.replace(/\/api$/, '');
    if (src.startsWith("/")) return `${baseWithoutApi}${src}`;
    return `${baseWithoutApi}/${src}`;
  };

  const [thumbnail, setThumbnail] = useState(() => {
    const thumb = product.thumbnail_full_url || product.thumbnail_url || product.thumbnail;
    const normalized = normalizeUrl(thumb);
    if (normalized) {
      return {
        preview: normalized,
        file: null,
        isExisting: true,
      };
    }
    return null;
  });

  const ensureArray = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const [images, setImages] = useState(() => {
    const raw = product.image_full_urls || product.image_urls || product.images;
    return ensureArray(raw).map((url) => ({
      preview: normalizeUrl(url) || url,
      file: null,
      isExisting: true,
    }));
  });

  const [productName, setProductName] = useState(product.name || "");
  const [slug, setSlug] = useState(product.slug || "");
  const [sku, setSku] = useState(product.sku || "");
  const [category, setCategory] = useState(toOption(product.category, "name"));
  const [subCategory, setSubCategory] = useState(toOption(product.subcategory, "name"));
  const [supplier, setSupplier] = useState(toOption(product.supplier, "retail_name"));
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [sellerOptions, setSellerOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [seller, setSeller] = useState(
    product.seller ? { value: product.seller, label: product.seller } : null
  );

  const toFeaturedOption = (val) => {
    if (val === undefined || val === null || val === "") return null;
    if (val === true || val === 1 || val === "1" || val === "yes" || val === "Yes") return { value: 1, label: "Yes" };
    return { value: 0, label: "No" };
  };

  const [featured, setFeatured] = useState(() => toFeaturedOption(product.featured));
  const [status, setStatus] = useState(
    product.status ? { value: product.status, label: product.status } : null
  );
  const [visibility, setVisibility] = useState(
    product.visibility ? { value: product.visibility, label: product.visibility } : null
  );

  const [price, setPrice] = useState(product.price || "");
  const [ComparePrice, setComparePrice] = useState(product.compare_price || "");
  const [cost, setCost] = useState(product.cost_per_item || "");
  const [discout, setDiscout] = useState(product.product_discount || "");
  const [tax, setTax] = useState(product.tax || "");
  const [commission, setCommission] = useState(product.commission || "");

  const trackQuantityOptions = [
    { value: 0, label: "No" },
    { value: 1, label: "Yes" },
  ];

  const toTrackOption = (val) => {
    const normalized = typeof val === "object" && val !== null && "value" in val ? val.value : val;
    if (normalized === 1 || normalized === "1" || normalized === true || normalized === "Yes" || normalized === "yes") {
      return trackQuantityOptions[1];
    }
    return trackQuantityOptions[0];
  };

  const [trackQuantity, setTrackQuantity] = useState(() => toTrackOption(product.track_quantity));
  const [stockQuantity, setStockQuantity] = useState(product.stock_quantity || ""); 
  const [stock, setStock] = useState(product.stock || ""); 
  const [weight, setWeight] = useState(product.weight || ""); 

  const [fullDescription, setFullDescription] = useState(product.description || "");
  const [shortDescription, setShortDescription] = useState(product.short_description || "");
  const [attributes, setAttributes] = useState(() => {
    if (Array.isArray(product.attributes)) {
      return product.attributes;
    }

    if (product.attributes && typeof product.attributes === "object") {
      return Object.entries(product.attributes).map(([key, value]) => ({
        name: key,
        value: Array.isArray(value) ? value.join(", ") : value,
      }));
    }

    return [{ name: "", value: "" }];
  });

  const [tags, setTags] = useState(() => {
    if (Array.isArray(product.tags)) return product.tags.join(", ");
    return product.tags || "";
  });
  const [productTags, setProductTags] = useState(() => {
    if (Array.isArray(product.product_tags)) return product.product_tags.join(", ");
    return product.product_tags || "";
  });
  const [metaTitle, setMetaTitle] = useState(product.meta_title || "");
  const [metaDescription, setMetaDescription] = useState(product.meta_description || "");
  const [metaKeywords, setMetaKeywords] = useState(() => {
    if (Array.isArray(product.meta_keywords)) return product.meta_keywords.join(", ");
    return product.meta_keywords || "";
  });

  const validateBasicDetails = () => {
    let valid = true;
    return valid;
  };

  const validateStep = () => {
    if (step === 1) {
      // Add image validation if needed
    }
    return true;
  };

  const nextStep = () => {
    if (step === 1) {
      const isValid = validateBasicDetails();
      if (!isValid) return;
    }
    if (validateStep()) setStep((prev) => Math.min(prev + 1, 5));
    else alert("Please fill all required fields before proceeding.");
  };
  
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  // Handle step click - allow clicking on any step
  const handleStepClick = (stepNumber) => {
    if (stepNumber === step) return;
    setStep(stepNumber);
  };

  const steps = [
    { label: "Product image", sub: "Enter product image details" },
    { label: "Basic details", sub: "Enter product basic details" },
    { label: "Pricing, Tax & Commission", sub: "Enter product financial details" },
    { label: "Inventory", sub: "Enter product inventory details" },
    { label: "Description & Attributes", sub: "Enter description & attributes details" },
  ];

  useEffect(() => {
    if (!trackQuantity) return;

    if (trackQuantity.value === 0) {
      setStockQuantity("-");
    } else {
      setStockQuantity((prev) => (prev === "-" ? "" : prev));
    }
  }, [trackQuantity]);

  const handleUpdateProduct = async () => {
    if (!validateStep()) {
      alert("Please fill all required fields before updating the product.");
      return;
    }

    const ensureCategoryWithId = async () => {
      const catLabel = getLabel(category);
      const catId = normalizeId(category);
      if (catId) return { id: catId, option: category };
      if (!catLabel) return { id: null, option: null };
      try {
        const created = await apiClient.post("/superadmin/categories", { name: catLabel });
        const data = created.data?.category || created.data?.data || created.data || {};
        const opt = { value: data.id ?? catLabel, label: data.name ?? catLabel };
        setCategoryOptions((prev) => [...prev, opt]);
        setCategory(opt);
        return { id: normalizeId(opt), option: opt };
      } catch (err) {
        console.error("Failed to auto-create category", err?.response?.data || err.message);
        return { id: null, option: category };
      }
    };

    const ensureSubCategoryWithId = async (resolvedCategoryId) => {
      const subLabel = getLabel(subCategory);
      const subId = normalizeId(subCategory);
      if (subId) return { id: subId, option: subCategory };
      if (!subLabel) return { id: null, option: null };
      const catId = resolvedCategoryId || normalizeId(category);
      if (!catId) return { id: null, option: subCategory };
      try {
        const created = await apiClient.post("/superadmin/subcategories", { name: subLabel, category_id: catId });
        const data = created.data?.subcategory || created.data?.data || created.data || {};
        const opt = { value: data.id ?? subLabel, label: data.name ?? subLabel, categoryId: data.category_id || catId };
        setSubCategoryOptions((prev) => [...prev, opt]);
        setSubCategory(opt);
        return { id: normalizeId(opt), option: opt };
      } catch (err) {
        console.error("Failed to auto-create subcategory", err?.response?.data || err.message);
        return { id: null, option: subCategory };
      }
    };

    const ensureSupplierWithId = async () => {
      const supLabel = getLabel(supplier);
      const supId = normalizeId(supplier);
      if (supId) return { id: supId, option: supplier };
      if (!supLabel) return { id: null, option: null };
      try {
        const created = await apiClient.post("/superadmin/suppliers", { name: supLabel, retail_name: supLabel });
        const data = created.data?.supplier || created.data?.data || created.data || {};
        const opt = { value: data.id ?? supLabel, label: data.retail_name || data.name || supLabel };
        setSupplierOptions((prev) => [...prev, opt]);
        setSupplier(opt);
        return { id: normalizeId(opt), option: opt };
      } catch (err) {
        console.error("Failed to auto-create supplier", err?.response?.data || err.message);
        return { id: null, option: supplier };
      }
    };

    setLoading(true);
    
    try {
      const ensuredCategory = await ensureCategoryWithId();
      const ensuredSubCategory = await ensureSubCategoryWithId(ensuredCategory.id);
      const ensuredSupplier = await ensureSupplierWithId();

      const productData = new FormData();
      const appendIfValue = (key, value) => {
        if (value !== undefined && value !== null && value !== "") {
          productData.append(key, value);
        }
      };

      appendIfValue("name", productName);
      appendIfValue("slug", (slug || productName).toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''));
      appendIfValue("sku", sku);
      appendIfValue("price", price);
      appendIfValue("status", status?.value);
      appendIfValue("featured", featured?.value);
      appendIfValue("visibility", visibility?.value);

      const catId = ensuredCategory.id ?? normalizeId(category);
      const subId = ensuredSubCategory.id ?? normalizeId(subCategory);
      const supId = ensuredSupplier.id ?? normalizeId(supplier);

      if (Number.isFinite(catId)) appendIfValue("category_id", catId);
      if (Number.isFinite(subId)) appendIfValue("subcategory_id", subId);
      appendIfValue("category_name", getLabel(category));
      appendIfValue("subcategory_name", getLabel(subCategory));
      if (Number.isFinite(supId)) appendIfValue("supplier_id", supId);

      appendIfValue("compare_price", ComparePrice);
      appendIfValue("cost_per_item", cost);
      appendIfValue("product_discount", discout);
      appendIfValue("tax", tax);
      appendIfValue("commission", commission);
      appendIfValue("track_quantity", trackQuantity?.value);

      const normalizedStockQty = Number.isFinite(Number(stockQuantity)) ? Number(stockQuantity) : 0;
      const normalizedStock = Number.isFinite(Number(stock)) ? Number(stock) : 0;
      productData.append("stock_quantity", normalizedStockQty);
      productData.append("stock", normalizedStock);
      appendIfValue("weight", weight);
      appendIfValue("description", fullDescription);
      appendIfValue("short_description", shortDescription);

      const attributesMap = attributes
        .filter((a) => a?.name && a.name.trim())
        .reduce((acc, a) => {
          acc[a.name.trim()] = (a.value ?? "").trim();
          return acc;
        }, {});
      if (Object.keys(attributesMap).length) {
        productData.append("attributes", JSON.stringify(attributesMap));
      }

      const toList = (val) => {
        if (Array.isArray(val)) return val;
        if (val === undefined || val === null) return [];
        if (typeof val === "string") {
          return val
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean);
        }
        try {
          return String(val)
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean);
        } catch {
          return [];
        }
      };

      toList(tags).forEach((t) => productData.append("tags[]", t));
      toList(productTags).forEach((t) => productData.append("product_tags[]", t));
      appendIfValue("meta_title", metaTitle);
      appendIfValue("meta_description", metaDescription);
      toList(metaKeywords).forEach((k) => productData.append("meta_keywords[]", k));

      if (thumbnail?.file) productData.append("thumbnail", thumbnail.file);
      images.forEach((img) => {
        if (img?.file) productData.append("images[]", img.file);
      });

      const id = product.id || product.product_id;
      const res = await apiClient.post(`/superadmin/products/${id}`, productData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const updatedFromApi = res?.data?.product || res?.data?.data || null;
      const mergedProduct = {
        ...product,
        ...updatedFromApi,
        name: productName,
        slug,
        sku,
        price,
        status: status?.value,
        featured: featured?.value,
        visibility: visibility?.value,
        category,
        subcategory: subCategory,
        supplier,
        compare_price: ComparePrice,
        cost_per_item: cost,
        product_discount: discout,
        tax,
        commission,
        track_quantity: trackQuantity?.value,
        stock_quantity: Number.isFinite(Number(stockQuantity)) ? Number(stockQuantity) : 0,
        stock: Number.isFinite(Number(stock)) ? Number(stock) : 0,
        weight,
        description: fullDescription,
        short_description: shortDescription,
        tags: Array.isArray(tags) ? tags : (typeof tags === "string" ? tags.split(",").map((t)=>t.trim()).filter(Boolean) : []),
        product_tags: Array.isArray(productTags) ? productTags : (typeof productTags === "string" ? productTags.split(",").map((t)=>t.trim()).filter(Boolean) : []),
        meta_title: metaTitle,
        meta_description: metaDescription,
        meta_keywords: Array.isArray(metaKeywords) ? metaKeywords : (typeof metaKeywords === "string" ? metaKeywords.split(",").map((t)=>t.trim()).filter(Boolean) : []),
      };

      setOpenSnackbar(true);
      setTimeout(() => {
        navigate("/product-details", { state: { product: mergedProduct } });
      }, 2000);
    } catch (err) {
      const details = err?.response?.data || err?.message;
      const validation = err?.response?.data?.errors;
      console.error("Failed to update product", details);
      if (validation && typeof validation === "object") {
        const messages = Object.entries(validation)
          .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join("; ") : msgs}`)
          .join("\n");
        alert(`Failed to update product:\n${messages}`);
      } else {
        alert("Failed to update product. Check console for details.");
      }
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "draft", label: "Draft" },
    { value: "published", label: "Published" },
    { value: "scheduled", label: "Scheduled" },
  ];

  const visibilityOptions = [
    { value: "hidden", label: "Hidden" },
    { value: "public", label: "Public" },
    { value: "block", label: "Block" },
    { value: "banned", label: "Banned" },
  ];

  const featuredOptions = [
    { value: 1, label: "Yes" },
    { value: 0, label: "No" },
  ];

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setThumbnail({
      file,
      preview: URL.createObjectURL(file),
      isExisting: false,
    });
  };

  const getThumbnailSrc = () => {
    if (!thumbnail) return null;
    if (typeof thumbnail === "string") return thumbnail;
    return thumbnail.preview;
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if(!files) return;

    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      isExisting: false,
    }));

    setImages((prev) => [...prev, ...newImages]);
  };

  const addAttribute = () => {
    setAttributes([...attributes, { name: "", value: "" }]);
  };

  const removeAttribute = (index) => {
    setAttributes(attributes.filter((_, i) => i !== index));
  };

  const updateAttribute = (index, field, value) => {
    const updated = [...attributes];
    updated[index][field] = value;
    setAttributes(updated);
  };

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [catsRes, suppliersRes] = await Promise.all([
          apiClient.get("/superadmin/categories"),
          apiClient.get("/superadmin/suppliers"),
        ]);

        const categoriesPayload = catsRes.data?.categories || catsRes.data?.data || catsRes.data || [];
        const categories = Array.isArray(categoriesPayload)
          ? categoriesPayload
          : Array.isArray(categoriesPayload?.data)
          ? categoriesPayload.data
          : [];
        const categoryOpts = categories.map((c) => ({ value: c.id, label: c.name }));
        setCategoryOptions(categoryOpts);

        const subcatsFromCats = categories.flatMap((c) => c.subcategories || []);
        const subcatOpts = subcatsFromCats.map((s) => ({ value: s.id, label: s.name, categoryId: s.category_id }));
        setSubCategoryOptions(subcatOpts);

        const suppliers = suppliersRes.data?.suppliers || suppliersRes.data?.data || suppliersRes.data || [];
        const supplierOpts = Array.isArray(suppliers)
          ? suppliers.map((s) => ({ value: s.id, label: s.retail_name || s.name }))
          : [];
        setSupplierOptions(supplierOpts);
        setSellerOptions(supplierOpts);
      } catch (err) {
        console.error("Failed to load dropdown options", err?.response?.data || err.message);
      }
    };

    loadOptions();
  }, [apiClient]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar(false);
  };

  // Handle cancel
  const handleCancel = () => {
    navigate("/product-details", { 
      state: { 
        product: product
      } 
    });
  };

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="product-details-main">
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MuiAlert
            severity="success"
            icon={<span style={{ fontWeight: "bold", fontSize: "18px", color:'rgba(51, 178, 51, 1' }}>✔</span>}
            sx={{
              backgroundColor: "transparent",
              backdropFilter: "blur(8px)",
              color: "#000",
              border: "2px solid rgba(51, 178, 51, 1)",
              borderRadius: "10px",
              boxShadow: "none",
              fontSize: "15px",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              height: '25px',
              width: '100%',
              textAlign: 'center',
              justifyContent: 'center',
            }}
          >
            Product updated successfully!
          </MuiAlert>
        </Snackbar>

        <DetailsHeaderBar
          title="Edit Product"
          subtitle="You can edit the product details to update the product details"
          breadcrumbs={[
            {label: "Home", path: "/manage-member"},
            {label: "Manage Product", path: "/manage-product"},
            {label: "Edit Product " }
          ]}
        />

        <div className="multi-form-container">
          <div className="progress-bar">
            {steps.map((item, i) => {
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isCompleted = step > stepNum;
              return (
                <div key={i} className="progress-step">
                  <button
                    type="button"
                    className={`circle ${
                      isCompleted ? "completed" : isActive ? "active" : ""
                    } clickable`}
                    onClick={() => handleStepClick(stepNum)}
                    disabled={isActive || loading}
                    title={`Go to Step ${stepNum}: ${item.label}`}
                  >
                    {isCompleted ? "✓" : stepNum}
                  </button>
                  <div className="step-text">
                    <p className={`step-label ${isActive ? "active-label" : ""}`}>
                      {item.label}
                    </p>
                    <p className={`step-subtext ${isActive ? "active-sub" : ""}`}>
                      {item.sub}
                    </p>
                  </div>
                  {i < steps.length - 1 && (
                    <div
                      className={`line ${
                        isCompleted ? "line-filled" : "line-empty"
                      }`}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="step-content">
            {/* Step 1: Product Image Details */}
            {step === 1 && (
              <div>
                <h3>Product Image Details</h3>
                <hr/>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Thumbnail<span className="required"></span></label>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleThumbnailChange}
                      disabled={loading}
                    />
                    
                    {thumbnail && (
                      <div style={{ marginTop: 10, position: 'relative', display: 'inline-block' }}>
                        <img
                          src={getThumbnailSrc()}
                          alt="Thumbnail Preview"
                          onClick={() => setPreviewImage(getThumbnailSrc())}
                          style={{
                            width: 150,
                            height: 150,
                            objectFit: "cover",
                            borderRadius: 6,
                            cursor: "pointer"
                          }}
                        />

                        <button
                          type="button"
                          onClick={() => {
                            if (!thumbnail.isExisting && thumbnail.preview) {
                              URL.revokeObjectURL(thumbnail.preview);
                            }
                            setThumbnail(null);
                          }}
                          style={{
                            position: "absolute",
                            top: -10,
                            right: -10,
                            background: "#e53935",
                            color: "#fff",
                            border: "none",
                            borderRadius: "50%",
                            width: 24,
                            height: 24,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                          }}
                          disabled={loading}
                        >
                          x
                        </button>
                      </div>
                    )}
                  </div>
                </div> 

                <div className="form-grid">
                  <div className="form-group">
                    <label>Product Image <span className="required"></span></label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImagesChange}
                      style={{maxWidth:"100%"}}
                      disabled={loading}
                    />

                    <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                      {images.map((img, index) => (
                        <div 
                          key={index}
                          style={{
                            position: "relative",
                            width: 120,
                            height: 120,
                          }}
                        >
                          <img 
                            src={img.preview} 
                            alt='product' 
                            onClick={() => setPreviewImage(img.preview)}
                            style={{
                              width: 120,
                              height: 120,
                              objectFit: "cover",
                              borderRadius: 6,
                              cursor:"pointer"
                            }} 
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (!img.isExisting && img.preview) {
                                URL.revokeObjectURL(img.preview);
                              }
                              setImages(images.filter((_, i) => i !== index));
                            }}
                            style={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              background: "#e53935",
                              color: "#fff",
                              border: "none",
                              borderRadius: "50%",
                              width: 24,
                              height: 24,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: 14,
                            }}
                            disabled={loading}
                          >
                            x
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>       
            )}

            {/* Step 2: Basic Details */}
            {step === 2 && (
              <div>
                <h3>Basic Details</h3>
                <hr/>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Product name <span className="required">*</span></label>
                    <input 
                      type="text" 
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      placeholder="Enter product name" 
                      disabled={loading}
                     maxLength={255} />
                  </div>

                  <div className="form-group">
                    <label>Product slug <span className="required">*</span></label>
                    <input 
                      type="text" 
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      placeholder="Enter slug" 
                      disabled={loading}
                     maxLength={255} />
                  </div>

                  <div className="form-group">
                    <label>Product SKU <span className="required"></span></label>
                    <input 
                      type="text" 
                      value={sku}
                      onChange={(e) => setSku(e.target.value)}
                      placeholder="Enter sku" 
                      disabled={loading}
                     maxLength={100} />
                  </div>

                  <div className="form-group">
                    <label>Product category <span className="required">*</span></label>
                    <CreatableSelect
                      options={categoryOptions}
                      value={category}
                      onChange={setCategory}
                      onCreateOption={(inputValue) => {
                        const newOption = {
                          value: inputValue.toLowerCase().replace(/\s+/g, "_"),
                          label: inputValue,
                        };
                        setCategoryOptions((prev) => [...prev, newOption]);
                        setCategory(newOption);
                      }}
                      placeholder="Select category"
                      isSearchable
                      classNamePrefix="custom-select"
                      isDisabled={loading}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: '1.17px solid rgba(209, 209, 209, 1)',
                          borderRadius: '17.6px',
                          background: 'rgba(253, 253, 253, 1)',
                          minHeight: '38px',
                          boxShadow: state.isFocused ? 'none' : 'none',
                          '&:hover': 'none',
                          fontSize: '14px',
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: '0 8px',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color:'black',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          marginTop: '2px',
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? '#e6f0ff'
                            : isFocused
                            ? '#e6f0ff'
                            : '#fff',
                          color: isSelected ? '#000' : '#000',
                          cursor: 'pointer',
                        }),
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Product subcategory <span className="required">*</span></label>
                    <CreatableSelect
                      options={subCategoryOptions}
                      value={subCategory}
                      onChange={setSubCategory}
                      onCreateOption={(inputValue) => {
                        const newOption = {
                          value: inputValue.toLowerCase().replace(/\s+/g, "_"),
                          label: inputValue,
                        };
                        setSubCategoryOptions((prev) => [...prev, newOption]);
                        setSubCategory(newOption);
                      }}
                      placeholder="Select subcategory"
                      isSearchable
                      classNamePrefix="custom-select"
                      isDisabled={loading}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: '1.17px solid rgba(209, 209, 209, 1)',
                          borderRadius: '17.6px',
                          background: 'rgba(253, 253, 253, 1)',
                          minHeight: '38px',
                          boxShadow: state.isFocused ? 'none' : 'none',
                          '&:hover': 'none',
                          fontSize: '14px',
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: '0 8px',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color:'black',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          marginTop: '2px',
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? '#e6f0ff'
                            : isFocused
                            ? '#e6f0ff'
                            : '#fff',
                          color: isSelected ? '#000' : '#000',
                          cursor: 'pointer',
                        }),
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Product supplier <span className="required">*</span></label>
                    <CreatableSelect
                      options={supplierOptions}
                      value={supplier}
                      onChange={setSupplier}
                      onCreateOption={(inputValue) => {
                        const newOption = {
                          value: inputValue.toLowerCase().replace(/\s+/g, "_"),
                          label: inputValue,
                        };
                        setSupplierOptions((prev) => [...prev, newOption]);
                        setSupplier(newOption);
                      }}
                      placeholder="Select supplier"
                      isSearchable
                      classNamePrefix="custom-select"
                      isDisabled={loading}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: '1.17px solid rgba(209, 209, 209, 1)',
                          borderRadius: '17.6px',
                          background: 'rgba(253, 253, 253, 1)',
                          minHeight: '38px',
                          boxShadow: state.isFocused ? 'none' : 'none',
                          '&:hover': 'none',
                          fontSize: '14px',
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: '0 8px',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color:'black',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          marginTop: '2px',
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? '#e6f0ff'
                            : isFocused
                            ? '#e6f0ff'
                            : '#fff',
                          color: isSelected ? '#000' : '#000',
                          cursor: 'pointer',
                        }),
                      }}
                    />
                  </div>

                  {/* <div className="form-group">
                    <label>Product seller <span className="required">*</span></label>
                    <CreatableSelect
                      options={sellerOptions}
                      value={seller}
                      onChange={setSeller}
                      onCreateOption={(inputValue) => {
                        const newOption = {
                          value: inputValue.toLowerCase().replace(/\s+/g, "_"),
                          label: inputValue,
                        };
                        setSellerOptions((prev) => [...prev, newOption]);
                        setSeller(newOption);
                      }}
                      placeholder="Select seller"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: '1.17px solid rgba(209, 209, 209, 1)',
                          borderRadius: '17.6px',
                          background: 'rgba(253, 253, 253, 1)',
                          minHeight: '38px',
                          boxShadow: state.isFocused ? 'none' : 'none',
                          '&:hover': 'none',
                          fontSize: '14px',
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: '0 8px',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color:'black',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          marginTop: '2px',
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? '#e6f0ff'
                            : isFocused
                            ? '#e6f0ff'
                            : '#fff',
                          color: isSelected ? '#000' : '#000',
                          cursor: 'pointer',
                        }),
                      }}
                    />
                  </div> */}

                  <div className="form-group">
                    <label>Product featured <span className="required">*</span></label>
                    <Select
                      options={featuredOptions}
                      value={featured}
                      onChange={setFeatured}
                      placeholder="Select featured"
                      isSearchable
                      classNamePrefix="custom-select"
                      isDisabled={loading}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: '1.17px solid rgba(209, 209, 209, 1)',
                          borderRadius: '17.6px',
                          background: 'rgba(253, 253, 253, 1)',
                          minHeight: '38px',
                          boxShadow: state.isFocused ? 'none' : 'none',
                          '&:hover': 'none',
                          fontSize: '14px',
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: '0 8px',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color:'black',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          marginTop: '2px',
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? '#e6f0ff'
                            : isFocused
                            ? '#e6f0ff'
                            : '#fff',
                          color: isSelected ? '#000' : '#000',
                          cursor: 'pointer',
                        }),
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Status <span className="required">*</span></label>
                    <Select
                      options={statusOptions}
                      value={status}
                      onChange={setStatus}
                      placeholder="Select status"
                      isSearchable
                      classNamePrefix="custom-select"
                      isDisabled={loading}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: '1.17px solid rgba(209, 209, 209, 1)',
                          borderRadius: '17.6px',
                          background: 'rgba(253, 253, 253, 1)',
                          minHeight: '38px',
                          boxShadow: state.isFocused ? 'none' : 'none',
                          '&:hover': 'none',
                          fontSize: '14px',
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: '0 8px',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color:'black',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          marginTop: '2px',
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? '#e6f0ff'
                            : isFocused
                            ? '#e6f0ff'
                            : '#fff',
                          color: isSelected ? '#000' : '#000',
                          cursor: 'pointer',
                        }),
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Visibility <span className="required">*</span></label>
                    <Select
                      options={visibilityOptions}
                      value={visibility}
                      onChange={setVisibility}
                      placeholder="Select visibility"
                      isSearchable
                      classNamePrefix="custom-select"
                      isDisabled={loading}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: '1.17px solid rgba(209, 209, 209, 1)',
                          borderRadius: '17.6px',
                          background: 'rgba(253, 253, 253, 1)',
                          minHeight: '38px',
                          boxShadow: state.isFocused ? 'none' : 'none',
                          '&:hover': 'none',
                          fontSize: '14px',
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: '0 8px',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color:'black',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          marginTop: '2px',
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? '#e6f0ff'
                            : isFocused
                            ? '#e6f0ff'
                            : '#fff',
                          color: isSelected ? '#000' : '#000',
                          cursor: 'pointer',
                        }),
                      }}
                    />
                  </div>
                </div>            
              </div>        
            )}

            {/* Step 3: Pricing, Tax & Commission */}
            {step === 3 && (
              <div>
                <h3>Pricing, Tax & Commission Details</h3>
                <hr/>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Price <span className="required">*</span></label>
                    <input 
                      type="text" 
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="Enter price" 
                      disabled={loading}
                     maxLength={255} />
                  </div>

                  <div className="form-group">
                    <label>Compare price <span className="required">*</span></label>
                    <input 
                      type="text" 
                      value={ComparePrice}
                      onChange={(e) => setComparePrice(e.target.value)}
                      placeholder="Enter compare price" 
                      disabled={loading}
                     maxLength={255} />
                  </div>

                  <div className="form-group">
                    <label>Cost <span className="required">*</span></label>
                    <input 
                      type="text" 
                      value={cost}
                      onChange={(e) => setCost(e.target.value)}
                      placeholder="Enter cost" 
                      disabled={loading}
                     maxLength={255} />
                  </div>

                  <div className="form-group">
                    <label>Discount <span className="required"></span></label>
                    <input 
                      type="text" 
                      value={discout}
                      onChange={(e) => setDiscout(e.target.value)}
                      placeholder="Enter discount %" 
                      disabled={loading}
                     maxLength={255} />
                  </div>

                  <div className="form-group">
                    <label>Tax <span className="required"></span></label>
                    <input 
                      type="text" 
                      value={tax}
                      onChange={(e) => setTax(e.target.value)}
                      placeholder="Enter tax %" 
                      disabled={loading}
                     maxLength={50} />
                  </div>

                  <div className="form-group">
                    <label>Commission <span className="required"></span></label>
                    <input 
                      type="text" 
                      value={commission}
                      onChange={(e) => setCommission(e.target.value)}
                      placeholder="Enter commission %" 
                      disabled={loading}
                     maxLength={50} />
                  </div>
                </div>
              </div>        
            )}

            {/* Step 4: Inventory */}
            {step === 4 && (
              <div>
                <h3>Inventory Details</h3>
                <hr/>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Track quantity <span className="required">*</span></label>
                    <Select
                      options={trackQuantityOptions}
                      value={trackQuantity}
                      onChange={setTrackQuantity}
                      placeholder="Select track quantity"
                      classNamePrefix="custom-select"
                      isDisabled={loading}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: '1.17px solid rgba(209, 209, 209, 1)',
                          borderRadius: '17.6px',
                          background: 'rgba(253, 253, 253, 1)',
                          minHeight: '38px',
                          boxShadow: state.isFocused ? '0 0 0px rgba(21, 21, 22, 0.4)' : 'none',
                          '&:hover': 'none',
                          fontSize: '14px',
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: '0 8px',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color:'black',
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color:'rgba(105, 105, 105, 1)',
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          marginTop: '2px',
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? '#e6f0ff'
                            : isFocused
                            ? '#e6f0ff'
                            : '#fff',
                          color: isSelected ? '#000' : '#000',
                          cursor: 'pointer',
                        }),
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Stock quantity <span className="required">*</span></label>
                    <input 
                      type="text" 
                      value={trackQuantity?.value === 0 ? "-" : stockQuantity}
                      disabled={trackQuantity?.value === 0 || loading}
                      onChange={(e) => setStockQuantity(e.target.value)}
                      placeholder={
                        trackQuantity?.value === 0
                          ? "-"
                          : "Enter stock quantity"
                      } 
                      style={{
                        backgroundColor: trackQuantity?.value === 0 ? "#f5f5f5" : "#fff",
                        cursor: trackQuantity?.value === 0 ? "not-allowed" : "text",
                      }}
                     maxLength={255} />
                  </div>

                  <div className="form-group">
                    <label>Stock <span className="required">*</span></label>
                    <input 
                      type="text" 
                      value={stock}
                      onChange={(e) => setStock(e.target.value)}
                      placeholder="Enter stock" 
                      disabled={loading}
                     maxLength={255} />
                  </div>

                  <div className="form-group">
                    <label>Weight <span className="required"></span></label>
                    <input 
                      type="text" 
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      placeholder="Enter weight" 
                      disabled={loading}
                     maxLength={255} />
                  </div>
                </div>
              </div>       
            )}

            {/* Step 5: Description & Attributes */}
            {step === 5 && (
              <div>
                <h3>Description & Attributes Details</h3>
                <hr/>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Full description <span className="required"></span></label>
                    <textarea
                      className="note-textarea"
                      rows="3"
                      value={fullDescription}
                      onChange={(e) => setFullDescription(e.target.value)}
                      placeholder="enter full description here"
                      style={{padding:"10px"}}
                      disabled={loading}
                     maxLength={65535} />  
                  </div>

                  <div className="form-group">
                    <label>Short description <span className="required"></span></label>
                    <textarea
                      className="note-textarea"
                      rows="3"
                      value={shortDescription}
                      onChange={(e) => setShortDescription(e.target.value)}
                      placeholder="enter short description here"
                      style={{padding:"10px"}}
                      disabled={loading}
                     maxLength={65535} />  
                  </div>

                  <div className="form-group">
                    <label>Attributes<span className="required">*</span></label>
                    {attributes.map((attr, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          gap: 10,
                          marginBottom: 8,
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Attribute name (e.g. Color)"
                          value={attr.name}
                          onChange={(e) => updateAttribute(index, "name", e.target.value)}
                          disabled={loading}
                         maxLength={255} />
                        <input
                          type="text"
                          placeholder="Attribute value (e.g. Black, Blue)"
                          value={attr.value}
                          onChange={(e) => updateAttribute(index, "value", e.target.value)}
                          disabled={loading}
                         maxLength={255} />
                        {attributes.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeAttribute(index)}
                            style={{
                              background: "#e53935",
                              color: "#fff",
                              border: "none",
                              borderRadius: 6,
                              padding: "6px 10px",
                              cursor: "pointer",
                            }}
                            disabled={loading}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addAttribute}
                      style={{
                        marginTop: 6,
                        background: "#1976d2",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 12px",
                        cursor: "pointer",
                      }}
                      disabled={loading}
                    >
                      + Add Attribute
                    </button>
                  </div>

                  <div className="form-group">
                    <label>Tags <span className="required"></span></label>
                    <textarea
                      className="note-textarea"
                      rows="3"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      placeholder="enter tags here"
                      style={{padding:"10px"}}
                      disabled={loading}
                     maxLength={65535} />  
                  </div>

                  <div className="form-group">
                    <label>Product tags <span className="required"></span></label>
                    <textarea
                      className="note-textarea"
                      rows="3"
                      value={productTags}
                      onChange={(e) => setProductTags(e.target.value)}
                      placeholder="enter product tags here"
                      style={{padding:"10px"}}
                      disabled={loading}
                     maxLength={65535} />  
                  </div>

                  <div className="form-group">
                    <label>Meta title <span className="required"></span></label>
                    <textarea
                      className="note-textarea"
                      rows="3"
                      value={metaTitle}
                      onChange={(e) => setMetaTitle(e.target.value)}
                      placeholder="enter meta title here"
                      style={{padding:"10px"}}
                      disabled={loading}
                     maxLength={255} />  
                  </div>

                  <div className="form-group">
                    <label>Meta description <span className="required"></span></label>
                    <textarea
                      className="note-textarea"
                      rows="3"
                      value={metaDescription}
                      onChange={(e) => setMetaDescription(e.target.value)}
                      placeholder="enter meta description here"
                      style={{padding:"10px"}}
                      disabled={loading}
                     maxLength={65535} />  
                  </div>

                  <div className="form-group">
                    <label>Meta keywords <span className="required"></span></label>
                    <textarea
                      className="note-textarea"
                      rows="3"
                      value={metaKeywords}
                      onChange={(e) => setMetaKeywords(e.target.value)}
                      placeholder="enter meta keywords here"
                      style={{padding:"10px"}}
                      disabled={loading}
                     maxLength={65535} />  
                  </div>
                </div>
              </div>          
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="btn-row">
            {step === 5 && (
              <button 
                className="btn prev" 
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
            )}

            {step > 1 && (
              <button 
                className="btn prev" 
                onClick={prevStep}
                disabled={loading}
              >
                Previous
              </button>
            )}
            {step < 5 && (
              <button 
                className="btn next" 
                onClick={nextStep}
                disabled={loading}
              >
                Next
              </button>
            )}
            {step === 5 && (
              <button 
                className="btn next" 
                onClick={handleUpdateProduct}
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Product'}
              </button>
            )}
          </div>
        </div>  
      </div>

      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div style={{ position: "relative" }}>
            <img
              src={previewImage}
              alt="Preview"
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                borderRadius: 10,
              }}
            />
            <button
              onClick={() => setPreviewImage(null)}
              style={{
                position: "absolute",
                top: -10,
                right: -10,
                background: "#e53935",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: 30,
                height: 30,
                cursor: "pointer",
                fontSize: 16,
                fontWeight: "bold",
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditProduct;
